import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useDebounce } from 'use-debounce';
import { toast } from 'react-toastify';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import Category from '@/components/Modal/Project/Category';
import Delete from '@/components/Modal/General/Delete';
import Loader from '@/components/ui/Loader/Loader';

import project from '@/api/project';
import icAddNew from '@/assets/icons/add-new-user.png';
import usePatch from '../../hooks/usePatch';
import { Pencil, Trash2 } from 'lucide-react';

const { getProductCategory, getProductGroup } = project();

export default function Categories() {
	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [newCode, setNewCategory] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [isDeleteCategory, setIsDeleteCategory] = useState(false);

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
		refetch,
	} = useQuery({
		queryKey: ['categories', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getProductCategory(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const { data: g } = useQuery({
		queryKey: ['project-group'],
		queryFn: () => getProductGroup({ max_size: true }),
	});

	const dataCount =
		useMemo(() => {
			const count = d?.data?.count || d?.data?.length;
			if (isFetched && count) {
				oC.current = count;
				return count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results || d?.data || [], [d]) || [];
	const categories = useMemo(() => g?.data, [g]) || [g];

	const {
		mutate: deleteCategory,
		isPending,
		isSuccess,
	} = usePatch({
		url: `categorycomponent/${isDeleteCategory?.id}`,
		method: 'delete',
		callback: () => {
			toast.success(`kategori deleted`);
			refetch();
			setIsDeleteCategory(false);
		},
		onError: () => {},
		showError: true,
	});

	const handleDelete = () => {
		if (!isDeleteCategory) return;
		deleteCategory();
	};

	const columns = [
		// {
		// 	field: 'id',
		// 	headerName: 'Nr',
		// 	filterable: false,
		// 	sortable: true,
		// 	width: 64,
		// 	renderCell: (index) => index.api.getRowIndexRelativeToVisibleRows(index.row.id + 1),
		// },
		{
			field: 'categoryname',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'group_groupname',
			headerName: 'Categorys',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
			valueGetter: (_, row) => {
				const { group_groupname } = row;
				return group_groupname || '-';
			},
		},
		{
			field: 'time_update',
			headerName: 'Oppdatert dato',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { time_update } = params.row;
				return (
					<div className='flex flex-col'>
						<span>{format(new Date(time_update), 'yyyy/MM/dd')}</span>
					</div>
				);
			},
		},
		{
			field: 'id',
			headerName: 'Status',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { status } = params.row;
				return (
					<div className='flex flex-col'>
						<span>{status === 1 ? 'Aktiv' : 'Inaktiv'}</span>
					</div>
				);
			},
		},
		{
			field: 'status',
			headerName: '',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { row } = params;
				return row.status === 1 ? (
					<button title="Set Inaktiv" aria-label='toggle password visibility' onClick={() => setIsDeleteCategory({ id: row.id, name: row.categoryname })}>
						<Trash2 size={16} />
					</button>
				) : (
					<button title="Set Aktiv" aria-label='toggle password visibility' onClick={() => setSelectedRow(row)}>
						<Pencil size={16} />
					</button>
				);
			},
		},
	];

	const onSaveCode = () => {
		refetch();
	};

	return (
		<>
			<BDHelmet title='Categories' description='BDS Samferdsel project categories' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk'
							onChange={(e) => {
								setSearch(e.target.value);
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
						<button
							onClick={() => setNewCategory(true)}
							title='Registrer ny bruker'
							className='relative flex items-center justify-center gap-2 min-w-[192px] h-10 btn'
						>
							<div className='flex items-center justify-center w-5 h-5 bg-white rounded-full'>
								<img src={icAddNew} />
							</div>
							Ny under-kategori
						</button>
					</div>
					<div>
						<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>brukere</span>
					</div>
					<div className='mx-auto'>
						{isPending && !isSuccess && <Loader />}

						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							// rowsPerPageOptions={[10, 20, 50]}
							className='h-full mx-auto mt-0 w-[780px]'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								cellClass += ' cell-fix';
								if (selectedRow?.id === params.row.id) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							pageSizeOptions={[10, 20, 50, 100]}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
							// filterModel={{
							// 	items: [
							// 		{
							// 			field: 'name',
							// 			operator: 'contains',
							// 			value: search || '',
							// 		},
							// 	],
							// }}
						/>
					</div>
				</div>
				{!!selectedRow && (
					<Category
						open={!!selectedRow}
						onSave={onSaveCode}
						data={selectedRow}
						onClose={() => setSelectedRow(false)}
						categories={categories}
					/>
				)}
				{!!newCode && <Category open={!!newCode} onSave={onSaveCode} onClose={() => setNewCategory(false)} categories={categories} />}
				{!!isDeleteCategory && (
					<Delete open={!!isDeleteCategory} data={isDeleteCategory} onSave={handleDelete} onClose={() => setIsDeleteCategory(false)} />
				)}
			</div>
		</>
	);
}
